import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { CheckCircle2, DollarSign, TrendingUp, Shield, Zap, ArrowRight } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

const plans = [
  {
    name: 'Starter',
    price: '₹0',
    period: '/month',
    desc: 'Perfect for new businesses and freelancers just getting started.',
    cta: 'Get started free',
    to: '/register',
    featured: false,
    features: [
      'Up to ₹5L/month processing',
      'Payment links & checkout page',
      'UPI + cards + net banking',
      'T+2 settlements',
      'Email support',
      'Basic dashboard analytics',
    ],
  },
  {
    name: 'Business',
    price: '₹1,999',
    period: '/month',
    desc: 'For growing businesses that need faster settlements and priority support.',
    cta: 'Start free trial',
    to: '/register',
    featured: true,
    features: [
      'Up to ₹50L/month processing',
      'Everything in Starter',
      'T+1 settlements',
      'Dedicated account manager',
      'Priority support (4h SLA)',
      'Advanced analytics & reports',
      'Custom payment page branding',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For high-volume businesses and enterprises with custom requirements.',
    cta: 'Contact sales',
    to: '/contact-us',
    featured: false,
    features: [
      'Unlimited processing volume',
      'Everything in Business',
      'Custom MDR negotiation',
      'Same-day settlement option',
      'Dedicated infrastructure',
      'SLA-backed uptime guarantee',
      'Custom API & webhook support',
    ],
  },
]

const mdrTable = [
  { method: 'UPI (above ₹2,000)', rate: '0%', notes: 'Zero MDR' },
  { method: 'UPI (below ₹2,000)', rate: '₹3 flat', notes: 'Per transaction' },
  { method: 'Debit Card (RuPay)', rate: '0%', notes: 'Zero MDR' },
  { method: 'Debit Card (Visa/MC)', rate: '0.40%', notes: 'Min ₹3' },
  { method: 'Credit Card (Domestic)', rate: '1.50%', notes: 'Min ₹5' },
  { method: 'Credit Card (International)', rate: '2.00%', notes: '+GST' },
  { method: 'Net Banking', rate: '₹10 flat', notes: 'Per transaction' },
  { method: 'Wallets', rate: '1.50%', notes: 'Min ₹3' },
  { method: 'EMI', rate: '1.50%', notes: 'Processing fee' },
]

export default function Pricing() {
  return (
    <>
      <style>{css}</style>
      <div className="pr-root">
        <Navbar />

        {/* Hero */}
        <section className="pr-hero">
          <div className="pr-container pr-hero-grid">
            <motion.div className="pr-hero-text"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <div className="pr-hero-circle"><DollarSign size={28} color="#fff" /></div>
              <span className="pr-sub">Pricing</span>
              <h1 className="pr-hero-h1">Simple, Transparent<br />Pricing</h1>
              <p className="pr-hero-p">
                No surprises. No hidden fees. Pay only for what you use — from zero to enterprise scale.
              </p>
              <div className="pr-hero-pills">
                {[{ icon: <Zap size={13}/>, t: 'Zero Setup Fee' }, { icon: <Shield size={13}/>, t: 'Secure Payments' },{ icon: <TrendingUp size={13}/>, t: 'T+1 Settlements' }].map(p => (
                  <span key={p.t} className="pr-hero-pill">{p.icon}{p.t}</span>
                ))}
              </div>
            </motion.div>
            <motion.div className="pr-hero-visual"
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
              <div className="pr-hv-card">
                <div className="pr-hv-header">
                  <div className="pr-hv-dots"><span/><span/><span/></div>
                  <span className="pr-hv-title">Cost Calculator</span>
                </div>
                <div className="pr-hv-example">
                  <div className="pr-hv-row"><span>Monthly GMV</span><strong>₹10,00,000</strong></div>
                  <div className="pr-hv-row"><span>Avg UPI fee</span><strong className="pr-hv-green">₹0</strong></div>
                  <div className="pr-hv-row"><span>Card fees (est.)</span><strong>₹4,000</strong></div>
                  <div className="pr-hv-divider" />
                  <div className="pr-hv-row pr-hv-total"><span>Total monthly cost</span><strong className="pr-hv-blue">₹4,000</strong></div>
                  <div className="pr-hv-sub">0.04% effective rate on ₹1Cr GMV</div>
                </div>
                <Link to="/register" className="pr-hv-cta">Start for free <ArrowRight size={14} /></Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Plans */}
        <section className="pr-section pr-bg-white">
          <div className="pr-container">
            <div className="pr-plans-grid">
              {plans.map((plan, i) => (
                <FadeUp key={plan.name} delay={i * 0.1}>
                  <div className={`pr-plan-card${plan.featured ? ' pr-plan-featured' : ''}`}>
                    {plan.featured && <div className="pr-plan-badge">Most Popular</div>}
                    <div className="pr-plan-name">{plan.name}</div>
                    <div className="pr-plan-price">
                      {plan.price}<span className="pr-plan-period">{plan.period}</span>
                    </div>
                    <p className="pr-plan-desc">{plan.desc}</p>
                    <Link to={plan.to} className={`pr-plan-cta${plan.featured ? ' pr-plan-cta-featured' : ''}`}>
                      {plan.cta}
                    </Link>
                    <ul className="pr-feat-list">
                      {plan.features.map((f) => (
                        <li key={f} className="pr-feat-item">
                          <CheckCircle2 size={16} className="pr-check-icon" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* MDR Table */}
        <section className="pr-section pr-bg-light">
          <div className="pr-container">
            <FadeUp>
              <div className="pr-section-center">
                <span className="pr-sub pr-green">MDR Rates</span>
                <h2 className="pr-section-h2">Transaction Fee Structure</h2>
                <p className="pr-para pr-para-c">
                  All rates are exclusive of 18% GST. Volume discounts available for Business and Enterprise plans.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="pr-table-wrap">
                <table className="pr-table">
                  <thead>
                    <tr>
                      <th>Payment Method</th>
                      <th>MDR Rate</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mdrTable.map((row) => (
                      <tr key={row.method}>
                        <td>{row.method}</td>
                        <td className="pr-td-rate">{row.rate}</td>
                        <td className="pr-td-note">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Cards illustration section */}
        <section className="pr-section pr-bg-white">
          <div className="pr-container">
            <FadeUp><div className="pr-section-center">
              <span className="pr-sub pr-green">Payment methods</span>
              <h2 className="pr-section-h2">Accept every way your customers want to pay</h2>
              <p className="pr-para pr-para-c">From UPI to credit cards, net banking to wallets — A1XPAY accepts 100+ payment methods across all major networks.</p>
            </div></FadeUp>
            <div className="pr-cards-grid">
              <FadeUp delay={0.05} className="pr-card-img-wrap">
                <motion.img src={`${B}/card-img-1.png`} alt="Payment card front"
                  whileHover={{ scale: 1.06, rotate: -2 }} transition={{ type: 'spring', stiffness: 200 }} />
              </FadeUp>
              <FadeUp delay={0.15} className="pr-card-img-wrap">
                <motion.img src={`${B}/card-img-2.png`} alt="Payment card back"
                  whileHover={{ scale: 1.06, rotate: 2 }} transition={{ type: 'spring', stiffness: 200 }} />
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Illustrated CTA */}
        <section className="pr-cta">
          <div className="pr-container pr-cta-flex">
            <FadeUp className="pr-cta-left">
              <span className="pr-sub pr-white">Start today</span>
              <h2 className="pr-cta-h2">No setup fee. No monthly commitment on Starter.</h2>
              <div className="pr-cta-btns">
                <Link to="/register" className="pr-btn-white">Open free account</Link>
                <Link to="/contact-us" className="pr-btn-ghost-w">Talk to sales</Link>
              </div>
            </FadeUp>
            <motion.div className="pr-cta-illu"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started" />
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.pr-root { font-family: 'Jost', sans-serif; background: #fff; }
.pr-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
@media (min-width:1200px) { .pr-container { padding: 0 2.5rem; } }
.pr-hero { background: linear-gradient(135deg, #f5faff 0%, #eef3ff 100%); padding: 100px 0 80px; }
.pr-hero-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .pr-hero-grid { grid-template-columns: 1fr 1fr; gap: 4rem; } }
.pr-hero-circle { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg,#1A4DBE,#06B6D4); display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem; box-shadow: 0 8px 24px rgba(26,77,190,0.3); }
.pr-sub { font-size: 0.875rem; font-weight: 700; color: #1A4DBE; margin-bottom: 0.875rem; display: block; text-transform: uppercase; letter-spacing: 0.08em; }
.pr-hero-pills { display: flex; gap: 0.625rem; flex-wrap: wrap; margin-top: 1.5rem; }
.pr-hero-pill { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.35rem 0.875rem; background: #fff; border: 1.5px solid #E8EBF4; border-radius: 50px; font-size: 0.8125rem; font-weight: 600; color: #1A4DBE; }
/* Pricing hero visual */
.pr-hero-visual { display: none; }
@media (min-width:1024px) { .pr-hero-visual { display: block; } }
.pr-hv-card { background: #fff; border-radius: 1.25rem; padding: 1.75rem; box-shadow: 0 20px 60px rgba(26,77,190,0.15); border: 1px solid #E8EBF4; }
.pr-hv-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
.pr-hv-dots { display: flex; gap: 5px; }
.pr-hv-dots span { width: 10px; height: 10px; border-radius: 50%; background: #E8EBF4; }
.pr-hv-dots span:first-child { background: #F87171; }
.pr-hv-dots span:nth-child(2) { background: #FBBF24; }
.pr-hv-dots span:nth-child(3) { background: #34D399; }
.pr-hv-title { font-size: 0.875rem; font-weight: 700; color: #0A1023; }
.pr-hv-example { display: flex; flex-direction: column; gap: 0.75rem; padding: 1rem; background: #f5faff; border-radius: 0.75rem; margin-bottom: 1.25rem; }
.pr-hv-row { display: flex; justify-content: space-between; font-size: 0.875rem; color: #2C3655; }
.pr-hv-row strong { font-weight: 700; color: #0A1023; }
.pr-hv-green { color: #22C55E !important; }
.pr-hv-blue { color: #1A4DBE !important; }
.pr-hv-divider { height: 1px; background: #E8EBF4; }
.pr-hv-total { font-weight: 700; }
.pr-hv-sub { font-size: 0.75rem; color: #94A3B8; }
.pr-hv-cta { display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.7rem 1.5rem; background: linear-gradient(135deg,#1A4DBE,#06B6D4); color: #fff; font-size: 0.9rem; font-weight: 600; border-radius: 50px; text-decoration: none; width: 100%; justify-content: center; transition: opacity 0.2s; }
.pr-hv-cta:hover { opacity: 0.9; }
.pr-green { color: #055C2D; }
.pr-white { color: rgba(255,255,255,0.85); }
.pr-hero-h1 { font-size: clamp(2rem, 4.5vw, 3.25rem); font-weight: 800; color: #0A1023; letter-spacing: -0.04em; line-height: 1.15; margin-bottom: 1.25rem; }
.pr-hero-p { font-size: 1.0625rem; color: #2C3655; line-height: 1.75; margin-bottom: 0; }
.pr-section { padding: 80px 0; }
.pr-bg-white { background: #fff; }
.pr-bg-light { background: #f5faff; }
.pr-plans-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width:768px) { .pr-plans-grid { grid-template-columns: repeat(3, 1fr); } }
.pr-plan-card { background: #f5faff; border: 1.5px solid #E8EBF4; border-radius: 1.25rem; padding: 2rem 1.75rem; display: flex; flex-direction: column; gap: 0.75rem; position: relative; transition: box-shadow 0.2s; }
.pr-plan-card:hover { box-shadow: 0 8px 32px rgba(47,65,129,0.10); }
.pr-plan-featured { background: #1A4DBE; border-color: #1A4DBE; }
.pr-plan-badge { position: absolute; top: -14px; left: 50%; transform: translateX(-50%); background: #055C2D; color: #fff; font-size: 0.75rem; font-weight: 700; padding: 0.25rem 0.875rem; border-radius: 999px; white-space: nowrap; }
.pr-plan-name { font-size: 1rem; font-weight: 700; color: #64748B; }
.pr-plan-featured .pr-plan-name { color: rgba(255,255,255,0.75); }
.pr-plan-price { font-size: 2.5rem; font-weight: 800; color: #0A1023; letter-spacing: -0.04em; }
.pr-plan-featured .pr-plan-price { color: #fff; }
.pr-plan-period { font-size: 1rem; font-weight: 500; color: #64748B; }
.pr-plan-featured .pr-plan-period { color: rgba(255,255,255,0.6); }
.pr-plan-desc { font-size: 0.9rem; color: #2C3655; line-height: 1.65; }
.pr-plan-featured .pr-plan-desc { color: rgba(255,255,255,0.8); }
.pr-plan-cta { display: flex; align-items: center; justify-content: center; padding: 0.7rem 1.5rem; font-size: 0.9375rem; font-weight: 600; color: #fff; background: #1A4DBE; border: 1.5px solid #1A4DBE; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; margin: 0.5rem 0; }
.pr-plan-cta:hover { background: #1540a8; }
.pr-plan-cta-featured { background: #fff; color: #1A4DBE; border-color: #fff; }
.pr-plan-cta-featured:hover { background: #f0f4ff; }
.pr-feat-list { list-style: none; padding: 0; margin: 0.5rem 0 0; display: flex; flex-direction: column; gap: 0.625rem; }
.pr-feat-item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: #2C3655; }
.pr-plan-featured .pr-feat-item { color: rgba(255,255,255,0.9); }
.pr-check-icon { color: #055C2D; flex-shrink: 0; }
.pr-plan-featured .pr-check-icon { color: rgba(255,255,255,0.8); }
.pr-section-center { text-align: center; margin-bottom: 3rem; }
.pr-section-h2 { font-size: clamp(1.75rem, 3.5vw, 2.25rem); font-weight: 700; color: #0A1023; letter-spacing: -0.03em; line-height: 1.2; margin-bottom: 1rem; }
.pr-para { font-size: 1rem; color: #2C3655; line-height: 1.75; margin-bottom: 1.5rem; }
.pr-para-c { text-align: center; margin-left: auto; margin-right: auto; max-width: 560px; }
.pr-table-wrap { overflow-x: auto; border-radius: 1rem; box-shadow: 0 4px 24px rgba(47,65,129,0.08); }
.pr-table { width: 100%; border-collapse: collapse; background: #fff; font-size: 0.9375rem; }
.pr-table th { background: #1A4DBE; color: #fff; padding: 0.875rem 1.25rem; text-align: left; font-size: 0.875rem; font-weight: 600; }
.pr-table th:first-child { border-radius: 1rem 0 0 0; }
.pr-table th:last-child { border-radius: 0 1rem 0 0; }
.pr-table td { padding: 0.875rem 1.25rem; border-bottom: 1px solid #F1F5FD; color: #2C3655; }
.pr-table tr:last-child td { border-bottom: none; }
.pr-table tr:hover td { background: #f5faff; }
.pr-td-rate { font-weight: 700; color: #0A1023; }
.pr-td-note { color: #64748B; font-size: 0.875rem; }
/* Payment methods card grid */
.pr-cards-grid { display: flex; justify-content: center; align-items: center; gap: 2rem; flex-wrap: wrap; padding-top: 1rem; }
.pr-card-img-wrap { display: flex; align-items: center; justify-content: center; }
.pr-card-img-wrap img { max-width: 280px; max-height: 200px; object-fit: contain; }
/* CTA */
.pr-cta { background: #1A4DBE; padding: 80px 0; position: relative; overflow: hidden; }
.pr-cta::before { content: ''; position: absolute; width: 400px; height: 400px; border-radius: 50%; background: rgba(255,255,255,0.05); top: -150px; right: -80px; }
.pr-cta-flex { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap; }
.pr-cta-left { flex: 1; min-width: 280px; }
.pr-cta-h2 { font-size: clamp(1.625rem, 3.5vw, 2.5rem); font-weight: 800; color: #fff; letter-spacing: -0.04em; margin-bottom: 2rem; }
.pr-cta-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
.pr-cta-illu { display: none; }
@media (min-width:900px) { .pr-cta-illu { display: block; width: 200px; flex-shrink: 0; } }
.pr-cta-illu img { max-width: 100%; filter: brightness(1.1) drop-shadow(0 8px 24px rgba(0,0,0,0.2)); }
.pr-btn-white { display: inline-flex; align-items: center; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #1A4DBE; background: #fff; border: 1.5px solid #fff; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; }
.pr-btn-white:hover { background: #f0f4ff; }
.pr-btn-ghost-w { display: inline-flex; align-items: center; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #fff; background: transparent; border: 1.5px solid rgba(255,255,255,0.55); border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; }
.pr-btn-ghost-w:hover { border-color: #fff; }
`
